import React, { useEffect, useState } from "react";
import { adminOrderApi } from "../services/api";

import {
  Box,
  Typography,
  Card,
  CardContent,
  MenuItem,
  Select,
  CircularProgress,
  Chip,
  Grid,
  Avatar
} from "@mui/material";

// =========================
// STATUS COLOR
// =========================
const getStatusColor = (status) => {
  switch (status) {
    case "delivered":
      return "success";
    case "shipped":
      return "info";
    case "processing":
      return "warning";
    case "pending":
      return "default";
    case "cancelled":
      return "error";
    default:
      return "default";
  }
};

// =========================
// GROUP ORDERS BY USER
// =========================
const groupOrdersByUser = (orders) => {

  return orders.reduce((acc, order) => {

    const userName =
      order.user
        ? `${order.user.firstName} ${order.user.lastName || ""}`
        : "Unknown User";

    if (!acc[userName]) {
      acc[userName] = [];
    }

    acc[userName].push(order);

    return acc;

  }, {});
};

const AdminOrders = () => {

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  // 🔥 DEFAULT OPEN
  const [openUsers, setOpenUsers] = useState({});

  useEffect(() => {
    fetchOrders();
  }, []);

  // =========================
  // FETCH ORDERS
  // =========================
  const fetchOrders = async () => {

    try {

      const res = await adminOrderApi.getAll();

      const ordersData = res.data.orders || [];

      setOrders(ordersData);

      // 🔥 AUTO OPEN ALL USERS
      const grouped = groupOrdersByUser(ordersData);

      const initialOpenState = {};

      Object.keys(grouped).forEach((key) => {
        initialOpenState[key] = true;
      });

      setOpenUsers(initialOpenState);

    } catch (err) {

      console.error("Fetch error:", err);

    } finally {

      setLoading(false);

    }
  };

  // =========================
  // TOGGLE USER
  // =========================
  const toggleUser = (userName) => {

    setOpenUsers((prev) => ({
      ...prev,
      [userName]: !prev[userName],
    }));

  };

  // =========================
  // UPDATE STATUS
  // =========================
  const handleStatusChange = async (id, status) => {

    try {

      setUpdatingId(id);

      await adminOrderApi.updateStatus(id, status);

      // 🔥 UPDATE UI
      setOrders((prev) =>
        prev.map((o) =>
          o._id === id
            ? { ...o, status }
            : o
        )
      );

    } catch (err) {

      console.error("Update failed:", err);

    } finally {

      setUpdatingId(null);

    }
  };

  // =========================
  // LOADING
  // =========================
  if (loading) {

    return (
      <Box textAlign="center" mt={5}>
        <CircularProgress />
      </Box>
    );

  }

  // =========================
  // GROUPED DATA
  // =========================
  const groupedOrders = groupOrdersByUser(orders);

  return (
    <Box
      maxWidth="1100px"
      margin="40px auto"
      padding="20px"
    >

      {/* PAGE TITLE */}
      <Typography
        variant="h4"
        fontWeight={700}
        mb={4}
      >
        Admin Orders Dashboard
      </Typography>

      {/* USER GROUPS */}
      {Object.entries(groupedOrders).map(([userName, userOrders]) => {

        const user = userOrders[0]?.user;

        const isOpen = openUsers[userName];

        return (
          <Box key={userName} mb={4}>

            {/* USER HEADER */}
            <Box
              onClick={() => toggleUser(userName)}
              sx={{
                background: "#f5f5f5",
                borderRadius: 3,
                p: 2,
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                boxShadow: 1,
                "&:hover": {
                  background: "#ececec",
                }
              }}
            >

              {/* LEFT */}
              <Box display="flex" gap={2} alignItems="center">

                <Avatar sx={{ bgcolor: "#8B0000" }}>
                  {user?.firstName?.charAt(0)}
                </Avatar>

                <Box>

                  <Typography
                    fontWeight={700}
                    fontSize="18px"
                  >
                    {userName}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    {user?.email}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Orders: {userOrders.length}
                    {" • "}
                    Total ₹
                    {userOrders.reduce(
                      (sum, o) => sum + o.totalAmount,
                      0
                    )}
                  </Typography>

                </Box>

              </Box>

              {/* RIGHT */}
              <Typography fontSize="20px">
                {isOpen ? "▲" : "▼"}
              </Typography>

            </Box>

            {/* ORDERS */}
            {isOpen && (

              <Box mt={2}>

                {userOrders.map((order) => (

                  <Card
                    key={order._id}
                    sx={{
                      mb: 2,
                      borderRadius: 3,
                      boxShadow: 2,
                      borderLeft: "5px solid #8B0000"
                    }}
                  >

                    <CardContent>

                      <Grid
                        container
                        spacing={2}
                        alignItems="center"
                      >

                        {/* ORDER INFO */}
                        <Grid item xs={12} md={4}>

                          <Typography fontWeight={700}>
                            Order ID
                          </Typography>

                          <Typography variant="body2">
                            {order._id}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mt={1}
                          >
                            {new Date(
                              order.createdAt
                            ).toLocaleString()}
                          </Typography>

                        </Grid>

                        {/* TOTAL */}
                        <Grid item xs={12} md={3}>

                          <Typography fontWeight={700}>
                            Amount
                          </Typography>

                          <Typography>
                            ₹{order.totalAmount}
                          </Typography>

                        </Grid>

                        {/* STATUS */}
                        <Grid item xs={12} md={5}>

                          <Box
                            display="flex"
                            alignItems="center"
                            gap={2}
                          >

                            <Chip
                              label={order.status}
                              color={getStatusColor(order.status)}
                            />

                            <Select
                              size="small"
                              value={order.status || "pending"}
                              onChange={(e) =>
                                handleStatusChange(
                                  order._id,
                                  e.target.value
                                )
                              }
                              disabled={
                                updatingId === order._id
                              }
                            >

                              <MenuItem value="pending">
                                Pending
                              </MenuItem>

                              <MenuItem value="processing">
                                Processing
                              </MenuItem>

                              <MenuItem value="shipped">
                                Shipped
                              </MenuItem>

                              <MenuItem value="delivered">
                                Delivered
                              </MenuItem>

                              <MenuItem value="cancelled">
                                Cancelled
                              </MenuItem>

                            </Select>

                            {updatingId === order._id && (
                              <CircularProgress size={20} />
                            )}

                          </Box>

                        </Grid>

                      </Grid>

                    </CardContent>

                  </Card>

                ))}

              </Box>

            )}

          </Box>
        );

      })}

    </Box>
  );
};

export default AdminOrders;